import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Checkbox,
  Tooltip,
  IconButton,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { issueList, issueSolveapi } from "../../../Services/Services";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import IssueForm from "./IssueForm";
import Popover from "@mui/material/Popover";
import DeleteIcon from "@mui/icons-material/Delete";

export default function IssueList() {
  const [issues, setIssues] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [issueSolve, setissueSolve] = useState(null);
  const [search, setSearch] = useState({
    vehicle_no: "",
    party_name: "",
    truck_owner: "",
  });

  useEffect(() => {
    const fetchIssue = async () => {
      const data = await issueList();
      setIssues(data);
    };
    fetchIssue();
  }, []);

  const handleDelete = async (Id) => {
    try {
      const response = await issueSolveapi(Id);
      setIssues(response);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearch((prev) => ({
      ...prev,
      [name]: value.toLowerCase(),
    }));
  };

  const filteredIssues = issues.filter((issue) => {
    return (
      (search.vehicle_no === "" ||
        (issue.truck_no &&
          issue.truck_no.toLowerCase().includes(search.vehicle_no))) &&
      (search.party_name === "" ||
        (issue.party_name &&
          issue.party_name.toLowerCase().includes(search.party_name))) &&
      (search.truck_owner === "" ||
        (issue.truck_owner &&
          issue.truck_owner.toLowerCase().includes(search.truck_owner)))
    );
  });

  // ----- Grouping by Truck No with rowSpan support -----
  const displayRows = useMemo(() => {
    const rows = [];
    let srNo = 1;

    const groups = {};
    filteredIssues.forEach((issue) => {
      const key = issue.truck_no || "Unknown";
      if (!groups[key]) groups[key] = [];
      groups[key].push(issue);
    });

    const sortedKeys = Object.keys(groups).sort((a, b) => {
      const totalA = groups[a].reduce(
        (sum, item) => sum + Number(item.recovery_amount || 0),
        0
      );

      const totalB = groups[b].reduce(
        (sum, item) => sum + Number(item.recovery_amount || 0),
        0
      );

      return totalB - totalA; // Highest Total First
    });

    sortedKeys.forEach((key) => {
      const issuesInGroup = groups[key];
      const total = issuesInGroup.reduce(
        (sum, i) => sum + Number(i.recovery_amount || 0),
        0
      );

      issuesInGroup.forEach((issue, idx) => {
        const isFirst = idx === 0;
        rows.push({
          type: "issue",
          data: issue,
          isFirst,
          sr: isFirst ? srNo : null,
          groupTotal: total,
          groupKey: key,
          groupCount: issuesInGroup.length, // for rowSpan
        });
        if (isFirst) srNo++;
      });

      // Total row for this group
      rows.push({
        type: "total",
        groupKey: key,
        groupTotal: total,
      });
    });

    return rows;
  }, [filteredIssues]);

  // ----- Print and Export (unchanged) -----
  const handlePrintHTML = () => {
    const originalTable = document.getElementById("issue-table");
    if (!originalTable) return;

    const clonedTable = originalTable.cloneNode(true);
    const removeIndexes = new Set();
    const headerRow = clonedTable.querySelector("thead tr");

    if (headerRow) {
      headerRow.querySelectorAll("th").forEach((th, idx) => {
        if (
          th.classList.contains("no-print") ||
          th.innerText.toLowerCase().includes("actions") ||
          th.querySelector("input[type='checkbox']")
        ) {
          removeIndexes.add(idx);
        }
      });
    }

    clonedTable.querySelectorAll("tr").forEach((row) => {
      const cells = Array.from(row.children);
      for (let i = cells.length - 1; i >= 0; i--) {
        const cell = cells[i];
        if (
          removeIndexes.has(i) ||
          cell.classList.contains("no-print") ||
          cell.querySelector("input[type='checkbox']")
        ) {
          cell.remove();
        }
      }
    });

    const printContent = clonedTable.outerHTML;
    const printWindow = window.open("", "", "width=1000,height=600");

    printWindow.document.write(`
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; padding: 0px 20px; }
            header { text-align: center; font-size: 20px; font-weight: bold; margin-bottom: 20px; color: #333; }
            footer { position: fixed; bottom: 10px; width: 100%; text-align: center; font-size: 12px; color: #333; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid black; padding: 8px; text-align: center; font-size: 12px; white-space: nowrap; }
            th { background-color: rgba(211, 211, 211, 0.42); color: #000; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .no-print { display: none !important; }
            @media print { html, body { zoom: 90%; } }
          </style>
        </head>
        <body>
          <header>SR ENTERPRICES</header>
          <h3 style="text-align: center;">Issue List</h3>
          ${printContent}
          <footer>Powered by : YASHVITECH IT SOLUTION PVT LTD</footer>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          <\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
  };

  const handleExportExcel = () => {
    const table = document.getElementById("issue-table");
    if (!table) {
      alert("Table not found!");
      return;
    }

    const rows = table.querySelectorAll("tr");
    if (!rows.length) {
      alert("No data to export");
      return;
    }

    const worksheetData = [];
    const removeIndexes = new Set();
    const headerCells = rows[0].querySelectorAll("th, td");

    headerCells.forEach((cell, idx) => {
      if (
        cell.classList.contains("no-print") ||
        cell.innerText.trim().toLowerCase() === "actions"
      ) {
        removeIndexes.add(idx);
      }
    });

    rows.forEach((row) => {
      const cells = row.querySelectorAll("th, td");
      const rowData = [];

      cells.forEach((cell, idx) => {
        if (!removeIndexes.has(idx)) {
          rowData.push(cell.innerText.trim());
        }
      });

      if (rowData.length) {
        worksheetData.push(rowData);
      }
    });

    if (!worksheetData.length) {
      alert("No valid data found to export.");
      return;
    }

    const headers = worksheetData[0];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    const columnWidths = headers.map((_, colIndex) => {
      const maxLength = worksheetData.reduce((max, row) => {
        const cell = row[colIndex];
        const len = cell ? cell.toString().length : 10;
        return Math.max(max, len);
      }, headers[colIndex].length);
      return { wch: maxLength + 2 };
    });
    worksheet["!cols"] = columnWidths;

    const blackBorder = {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    };

    headers.forEach((_, colIdx) => {
      const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: colIdx })];
      if (cell) {
        cell.s = {
          fill: { fgColor: { rgb: "FFFF99" } },
          font: { bold: true, color: { rgb: "000000" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: blackBorder,
        };
      }
    });

    for (let r = 1; r < worksheetData.length; r++) {
      for (let c = 0; c < headers.length; c++) {
        const cell = worksheet[XLSX.utils.encode_cell({ r, c })];
        if (cell) {
          cell.s = {
            alignment: { horizontal: "center", vertical: "center" },
            border: blackBorder,
          };
        }
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Issue Data");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "IssueList.xlsx");
  };

  return (
    <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
      {/* Search & Action Buttons - unchanged */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <TextField
          label="Search Truck No"
          name="vehicle_no"
          value={search.vehicle_no}
          onChange={handleSearchChange}
          size="small"
          sx={{ width: 200, mr: 2 }}
        />
        <TextField
          label="Search Party Name"
          name="party_name"
          value={search.party_name}
          onChange={handleSearchChange}
          size="small"
          sx={{ width: 200, mr: 2 }}
        />
        <TextField
          label="Search Truck Owner"
          name="truck_owner"
          value={search.truck_owner}
          onChange={handleSearchChange}
          size="small"
          sx={{ width: 200, mr: 2 }}
        />
        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenAddModal(true)}
            size="small"
          >
            Add Issue
          </Button>
          <Button
            variant="outlined"
            onClick={handleExportExcel}
            size="small"
            sx={{ minWidth: 40, height: 40, p: 0 }}
          >
            <FileDownloadIcon fontSize="small" />
          </Button>
          <Button
            variant="outlined"
            onClick={handlePrintHTML}
            size="small"
            sx={{ minWidth: 40, height: 40, p: 0 }}
          >
            <PrintIcon fontSize="small" />
          </Button>
        </Box>
      </Box>

      {/* Table Container */}
      <TableContainer
        sx={{
          overflowX: "auto",
          overflowY: "auto",
          maxHeight: "650px",
          mt: 2,
        }}
      >
        <Table
          size="small"
          id="issue-table"
          sx={{
            tableLayout: "auto",
            minWidth: "100%",
            "& td, & th": {
              textAlign: "center",
              whiteSpace: "nowrap",
              padding: "4px 8px",
              fontSize: "0.8125rem",
              borderBottom: "1px solid #ccc",
            },
          }}
        >
          <TableHead
            sx={{
              "& th": {
                fontWeight: "bold",
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#2196f3" : "#333",
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                position: "sticky",
                top: 0,
                zIndex: 1,
              },
            }}
          >
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>Truck No</TableCell>
              <TableCell>Party Name</TableCell>
              <TableCell>Freight ID</TableCell>
              <TableCell>Branch Name</TableCell>
              <TableCell>Truck Owner</TableCell>
              <TableCell>Recovery Amount</TableCell>
              <TableCell>Description</TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  right: 0,
                  zIndex: 3,
                  backgroundColor: "#f5f5f5",
                }}
                className="no-print"
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {displayRows.length > 0 ? (
              displayRows.map((row, index) => {
                if (row.type === "issue") {
                  const issue = row.data;
                  const bgColor = index % 2 === 0 ? "#fafafa" : "#ffffff";
                  return (
                    <TableRow
                      key={issue.id || `issue-${index}`}
                      sx={{
                        backgroundColor: bgColor,
                        "&:hover": { backgroundColor: "#f5f5f5" },
                      }}
                    >
                      {row.isFirst ? (
                        <>
                          <TableCell
                            rowSpan={row.groupCount}
                            sx={{
                              verticalAlign: "middle",
                              textAlign: "center",
                            }}
                          >
                            {row.sr}
                          </TableCell>

                          <TableCell
                            rowSpan={row.groupCount}
                            sx={{
                              verticalAlign: "middle",
                              textAlign: "center",
                            }}
                          >
                            {issue.truck_no || "-"}
                          </TableCell>
                        </>
                      ) : null}

                      <TableCell>{issue.party_name || "-"}</TableCell>
                      <TableCell>{issue.freight_id || "-"}</TableCell>
                      <TableCell>{issue.branch_name || "-"}</TableCell>
                      <TableCell>{issue.truck_owner || "-"}</TableCell>
                      <TableCell>
                        ₹ {Number(issue.recovery_amount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: "200px",
                          minWidth: "150px",
                          textAlign: "left",
                        }}
                      >
                        <Tooltip
                          title={issue.description || "-"}
                          arrow
                          placement="top"
                          enterDelay={500}
                        >
                          <Box
                            sx={{
                              maxHeight: "40px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              padding: "4px",
                              wordBreak: "break-word",
                              whiteSpace: "pre-wrap",
                              fontSize: "0.8rem",
                              lineHeight: "1.2",
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "#f0f0f0",
                                borderRadius: "4px",
                              },
                            }}
                          >
                            {issue.description && issue.description.length > 100
                              ? `${issue.description.substring(0, 100)}...`
                              : issue.description || "-"}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          position: "sticky",
                          right: 0,
                          zIndex: 3,
                          backgroundColor: bgColor,
                        }}
                        className="no-print"
                      >
                        <Checkbox
                          size="small"
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setissueSolve(issue.issue_id);
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                } else {
                  // Total row for the group
                  return (
                    <TableRow
                      key={`total-${row.groupKey}`}
                      sx={{
                        backgroundColor: "#e3f2fd",
                        fontWeight: "bold",
                      }}
                    >
                      <TableCell />
                      <TableCell>Total</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell>
                        ₹ {Number(row.groupTotal || 0).toFixed(2)}
                      </TableCell>
                      <TableCell />
                      <TableCell
                        sx={{
                          position: "sticky",
                          right: 0,
                          zIndex: 3,
                          backgroundColor: "#e3f2fd",
                        }}
                        className="no-print"
                      />
                    </TableRow>
                  );
                }
              })
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No records found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Issue Dialog */}
      <Dialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Add New Issue</DialogTitle>
        <DialogContent dividers>
          <IssueForm setIssues={setIssues} setOpenAddModal={setOpenAddModal} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddModal(false)} color="error">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setissueSolve(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" gutterBottom>
            Are you sure you want to mark this issue as solved?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setissueSolve(null);
              }}
            >
              No
            </Button>
            <Button
              color="primary"
              size="small"
              variant="contained"
              onClick={async () => {
                try {
                  await handleDelete(issueSolve);
                } finally {
                  setAnchorEl(null);
                  setissueSolve(null);
                }
              }}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}